import { createFileRoute, Link } from "@tanstack/react-router";
import { pageSchemaScript, PERSON_ID } from "@/seo/schema";
import { PageShell } from "@/components/PageShell";
import { SectionHeader } from "@/components/SectionHeader";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, ShieldCheck, MapPin, Radar, Camera, DoorOpen, Building2 } from "lucide-react";
import solSecurity from "@/assets/sol-security.jpg";

import { ogUrl } from "@/lib/ogSections";

const SITE_ORIGIN = "https://arifkhanglobal.com";
const URL = `${SITE_ORIGIN}/locations/security-consultant-delhi-ncr`;
const TITLE = "Security Consultant in Delhi NCR | Arif Khan";
const DESCRIPTION =
  "Independent security consultant in Delhi NCR — CCTV, command centre, access control, perimeter and boom-barrier design for homes and corporates.";


const WHO_FOR = [
  "Residential and farmhouse owners across Delhi, Gurugram, Noida, Faridabad and Ghaziabad",
  "Corporate HQs and global capability centres planning a multi-floor security stack",
  "Developers and RWAs needing a project-wide common-area security and ANPR design",
  "Institutions and government clients requiring command-centre design and SOPs",
];

const PLAN = [
  { Icon: Camera, title: "CCTV & VMS", desc: "Camera coverage, retention, analytics and a hardened VMS — designed against floor plates, not against catalogues." },
  { Icon: DoorOpen, title: "Access control", desc: "OSDP readers, mobile credentials, biometrics where it matters, governed by one policy and full audit logging." },
  { Icon: Radar, title: "Perimeter & gates", desc: "Layered detection, thermal + analytics, ANPR and boom barriers sized to actual peak-hour cycle counts." },
  { Icon: Building2, title: "Command centre", desc: "Operator console, SOPs, escalation paths and video-wall layouts designed before specifying any display." },
];

const MISTAKES = [
  "CCTV procured by count rather than by coverage",
  "Wiegand readers in a new install — instead of OSDP",
  "Boom barriers under-specified for actual peak-hour cycle count",
  "Command centre built as a furniture exercise, not a workflow",
  "No AMC structure or operator SOPs — system degrades within 18 months",
];

const FAQS = [
  { q: "What does a security consultant actually deliver?", a: "Independent design — CCTV coverage drawings, access-control policy, perimeter strategy, command-centre layout, SOPs and a vendor-agnostic BOQ. We start with the site, risk and operating requirement, then select the appropriate technology and equipment. Altima Global can supply, integrate, test and commission the resulting solution where required." },
  { q: "How is this different from a system integrator's design?", a: "An integrator's design is shaped by the brands they sell. Independent consulting starts from your risk profile, floor plans and operations, then selects technology — so you get the right system, not the most-stocked one." },
  { q: "Do you cover residential, commercial and government projects in NCR?", a: "Yes. We support private residences and farmhouses, corporate HQs, developer projects, institutions and government infrastructure across Delhi, Gurugram, Noida, Faridabad and Ghaziabad." },
  { q: "How early should we bring in a security consultant?", a: "At architectural design — before electrical layouts and BOQs are frozen. Camera locations, cabling pathways and the equipment room are easiest (and cheapest) to get right during civil work." },
];

const RELATED = [
  { to: "/solutions/security-infrastructure", label: "Security Infrastructure solution" },
  { to: "/solutions/cctv-command-centre", label: "CCTV & Command Centre" },
  { to: "/solutions/access-control-biometrics", label: "Access Control & Biometrics" },
  { to: "/solutions/perimeter-protection", label: "Perimeter Protection" },
  { to: "/solutions/entrance-gate-parking-automation", label: "Entrance, Gate & Parking Automation" },
  { to: "/ask-arif/how-to-choose-a-security-integrator", label: "How to choose a security integrator" },
] as const;

export const Route = createFileRoute("/locations/security-consultant-delhi-ncr")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { property: "og:image", content: ogUrl(solSecurity) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: ogUrl(solSecurity) },
      { name: "geo.region", content: "IN-DL" },
      { name: "geo.placename", content: "Delhi NCR" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      pageSchemaScript(
        {
          path: "/locations/security-consultant-delhi-ncr",
          type: "WebPage",
          name: TITLE,
          description: DESCRIPTION,
          breadcrumb: [{ name: "Locations", url: "/locations" }, { name: "Security Consultant in Delhi NCR" }],
          extra: { about: { "@id": `${URL}#service` }, mainEntity: { "@id": `${URL}#service` } },
        },
        [
          {
            "@type": "Service",
            "@id": `${URL}#service`,
            name: "Security Consultant — Delhi NCR",
            serviceType: "Security Infrastructure & Command Centre Consultancy",
            provider: { "@id": PERSON_ID },
            areaServed: [{ "@type": "City", name: "New Delhi" }, { "@type": "City", name: "Gurugram" }, { "@type": "City", name: "Noida" }, { "@type": "City", name: "Faridabad" }, { "@type": "City", name: "Ghaziabad" }],
            url: URL,
            description: DESCRIPTION,
          },
          {
            "@type": "FAQPage",
            "@id": `${URL}#faq`,
            url: URL,
            isPartOf: { "@id": `${URL}#webpage` },
            mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          },
        ],
      ),
    ],
  }),
});

function Page() {
  return (
    <PageShell>
      <section className="relative pt-40 pb-24 border-b border-border bg-surface overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div aria-hidden className="absolute inset-0 opacity-50" style={{ backgroundImage: `url(${solSecurity})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(40px) saturate(1.1)", transform: "scale(1.15)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/85 to-surface/60" />
        <div className="container-page relative">
          <div className="flex items-center gap-2 text-ink-soft">
            <MapPin className="h-4 w-4" />
            <span className="eyebrow">Delhi NCR · Locations</span>
          </div>
          <h1 className="mt-6 text-balance text-5xl md:text-7xl font-semibold tracking-tight leading-[0.95] max-w-4xl">
            Security Consultant in Delhi&nbsp;NCR
          </h1>
          <p className="mt-6 max-w-2xl text-xl text-ink-soft leading-relaxed">
            Independent security infrastructure design for residences, corporates, developers and institutions across Delhi, Gurugram, Noida, Faridabad and Ghaziabad. CCTV, command centre, access, perimeter and ANPR — engineered before any vendor pitches.
          </p>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><SectionHeader eyebrow="Who this is for" title="Owners, operators and design teams in NCR." /></div>
          <div className="lg:col-span-7 grid gap-4">
            {WHO_FOR.map((w, i) => (
              <Reveal key={w} delay={i * 0.04}>
                <div className="flex items-start gap-4 rounded-xl border border-border bg-surface p-5">
                  <div className="font-mono text-xs text-ink-soft w-8 pt-0.5">0{i + 1}</div>
                  <div className="text-sm text-foreground leading-relaxed">{w}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-surface border-y border-border">
        <div className="container-page">
          <SectionHeader eyebrow="What we plan" title="A coherent security stack — not a procurement list." description="From floor plate to command centre, designed against your operations and signed off vendor-agnostically." />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLAN.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-border bg-background p-6">
                  <p.Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  <h3 className="mt-6 text-lg font-semibold tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><SectionHeader eyebrow="Common mistakes" title="What we see go wrong on NCR security projects." /></div>
          <div className="lg:col-span-7 grid gap-4">
            {MISTAKES.map((m, i) => (
              <Reveal key={m} delay={i * 0.04}>
                <div className="flex items-start gap-4 rounded-xl border border-border bg-surface p-5">
                  <div className="font-mono text-xs text-ink-soft w-8 pt-0.5">{String(i + 1).padStart(2, "0")}</div>
                  <div className="text-sm text-foreground leading-relaxed">{m}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-surface border-y border-border">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><SectionHeader eyebrow="Related services" title="Where this connects." /></div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {RELATED.map((r) => (
              <Link key={r.to} to={r.to as "/"} className="group flex items-center justify-between rounded-xl border border-border bg-background p-5 hover:border-border-strong hover:shadow-card transition-all">
                <span className="text-sm font-medium">{r.label}</span>
                <ArrowRight className="h-4 w-4 text-ink-soft group-hover:text-primary transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><SectionHeader eyebrow="FAQ" title="Questions we hear from NCR clients." /></div>
          <div className="lg:col-span-7 grid gap-4">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.05}>
                <details className="group rounded-xl border border-border bg-background p-5 open:border-border-strong open:shadow-card transition-all">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-medium">
                    <span>{f.q}</span>
                    <ShieldCheck className="h-4 w-4 mt-1 text-ink-soft group-open:text-primary transition-colors" />
                  </summary>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-border bg-surface">
        <div className="container-page flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight max-w-2xl text-balance">Planning a security build in Delhi NCR?</h2>
            <p className="mt-3 text-ink-soft">Share floor plans, project type and current stage. An independent, vendor-agnostic review before procurement.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={`mailto:arif@arifkhanglobal.com?subject=${encodeURIComponent("Security Consultant — Delhi NCR")}`} className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:shadow-elevated transition-shadow">
              Email Your Requirement <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-border-strong bg-background px-7 py-3.5 text-sm font-medium hover:border-primary hover:text-primary transition-colors">
              Let's Talk
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
